import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CreditCard, UserCheck, Clock, ShieldAlert } from 'lucide-react';
import TopNav from '@/components/TopNav';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useLanguage } from '@/i18n/LanguageContext';

const Account = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [membershipStatus, setMembershipStatus] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      const [profileRes, membershipRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.from('membership_applications').select('status').eq('user_id', user.id).single(),
      ]);
      if (!profileRes.error && profileRes.data) setProfile(profileRes.data);
      setMembershipStatus(membershipRes.data?.status ?? null);
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const handleSubscribe = () => navigate('/subscribe');
  const remainingRequests = Math.max(0, 10 - (profile?.trial_requests_used || 0));
  const trialDaysLeft = profile?.trial_start_date ? Math.max(0, 7 - Math.floor((Date.now() - new Date(profile.trial_start_date).getTime()) / (1000 * 60 * 60 * 24))) : 0;

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/20">
      <TopNav user={user} onLogout={signOut} />
      <div className="container mx-auto max-w-4xl py-8 px-4">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('account.info')}</CardTitle>
              <CardDescription>{t('account.manageAccount')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <LabelSpan className="text-sm font-medium">{t('account.username')}</LabelSpan>
                <p className="text-lg">{profile?.username}</p>
              </div>
              <div>
                <LabelSpan className="text-sm font-medium">{t('account.accountStatus')}</LabelSpan>
                <div className="mt-2">
                  {profile?.is_subscribed ? <Badge variant="default">{t('account.subscribed')}</Badge> : <Badge variant="secondary">{t('account.trial')}</Badge>}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('account.membershipStatus')}</CardTitle>
              <CardDescription>{t('account.membershipDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              {membershipStatus === 'active' && profile?.is_subscribed && <div className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-green-600" /><span className="font-medium text-green-700">{t('account.membershipActive')}</span></div>}
              {membershipStatus === 'active' && !profile?.is_subscribed && remainingRequests > 0 && trialDaysLeft > 0 && <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-blue-600" /><span className="font-medium text-blue-700">Trial ({remainingRequests} requests left, {trialDaysLeft} days left)</span></div>}
              {membershipStatus === 'active' && !profile?.is_subscribed && (remainingRequests === 0 || trialDaysLeft === 0) && <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-orange-600" /><span className="font-medium text-orange-700">Trial Ended</span></div>}
              {membershipStatus === 'pending' && <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-yellow-600" /><span className="font-medium text-yellow-700">{t('account.pendingApproval')}</span></div>}
              {membershipStatus === 'blocked' && <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-destructive" /><span className="font-medium text-destructive">{t('account.accountBlocked')}</span></div>}
              {membershipStatus === null && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{t('account.noMembership')}</p>
                  <Button asChild variant="outline"><Link to="/member-register">{t('account.applyMembership')}</Link></Button>
                </div>
              )}
            </CardContent>
          </Card>

          {!profile?.is_subscribed && (
            <Card>
              <CardHeader>
                <CardTitle>{t('account.trialStatus')}</CardTitle>
                <CardDescription>{t('account.currentTrialUsage')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{t('account.remainingRequests')}</span>
                    <span className="text-2xl font-bold text-primary">{remainingRequests} / 10</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div className="bg-primary h-3 rounded-full transition-all" style={{ width: `${((10 - remainingRequests) / 10) * 100}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('account.trialDaysLeft')}</span>
                    <span className="text-2xl font-bold text-primary">{trialDaysLeft} {t('account.days')}</span>
                  </div>
                </div>
                {(remainingRequests === 0 || trialDaysLeft === 0) && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                    <p className="text-sm text-destructive font-medium">{t('account.trialEndedMsg')}</p>
                  </div>
                )}
                <Button onClick={handleSubscribe} className="w-full" size="lg">
                  <CreditCard className="mr-2 h-4 w-4" />{t('account.subscribeNow')}
                </Button>
              </CardContent>
            </Card>
          )}

          {profile?.is_subscribed && profile?.subscription_end_date && (
            <Card>
              <CardHeader>
                <CardTitle>{t('account.subscription')}</CardTitle>
                <CardDescription>{t('account.subscriptionDetails')}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{t('account.nextBilling')} {formatDistanceToNow(new Date(profile.subscription_end_date), { addSuffix: true })}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>{t('account.accountActions')}</CardTitle></CardHeader>
            <CardContent><Button variant="outline" onClick={signOut} className="w-full">{t('account.logOut')}</Button></CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const LabelSpan = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={className}>{children}</span>
);

export default Account;
